import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, ArrowRight,
} from "lucide-react";

// WMO weather codes → label, icon, and whether it links to the rainy day guide.
const CONDITIONS = [
  { codes: [0], label: "CLEAR SKY", Icon: Sun },
  { codes: [1, 2], label: "PARTLY CLOUDY", Icon: CloudSun },
  { codes: [3], label: "OVERCAST", Icon: Cloud },
  { codes: [45, 48], label: "FOGGY", Icon: CloudFog },
  { codes: [51, 53, 55, 56, 57], label: "DRIZZLING", Icon: CloudDrizzle, rain: true },
  { codes: [61, 63, 65, 66, 67], label: "RAINING", Icon: CloudRain, rain: true },
  { codes: [71, 73, 75, 77, 85, 86], label: "SNOWING", Icon: CloudSnow, snow: true },
  { codes: [80, 81, 82], label: "RAIN SHOWERS", Icon: CloudRain, rain: true },
  { codes: [95, 96, 99], label: "THUNDERSTORM", Icon: CloudLightning, rain: true },
];

const lookup = (code) => CONDITIONS.find((c) => c.codes.includes(code)) || CONDITIONS[2];

const useWeather = () => {
  const [wx, setWx] = useState(null);
  useEffect(() => {
    // Direct from the visitor's browser — Open-Meteo is keyless and CORS-open,
    // so this works even where the backend has no outbound internet access.
    fetch(
      "https://api.open-meteo.com/v1/forecast?latitude=55.6761&longitude=12.5683&current=temperature_2m,weather_code"
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (j && j.current) setWx({ temp: Math.round(j.current.temperature_2m), code: j.current.weather_code });
      })
      .catch(() => {});
  }, []);
  return wx;
};

// Small pill for the homepage hero badge row.
// `placeholder` renders a muted "loading" pill instead of nothing — used where
// the badge sits at the very top of a page, so the layout never jumps.
export const WeatherBadge = ({ placeholder = false }) => {
  const wx = useWeather();
  if (!wx) {
    if (!placeholder) return null;
    return (
      <span
        data-testid="weather-badge-loading"
        className="inline-flex items-center gap-2 opacity-40"
      >
        <Sun size={14} className="text-[#DF3226]" />
        <span className="font-body font-800 text-xs tracking-[0.2em]">CPH NOW · —</span>
      </span>
    );
  }
  const { label, Icon, rain } = lookup(wx.code);

  const inner = (
    <>
      <Icon size={15} className={rain ? "text-[#1E3A8A]" : "text-[#DF3226]"} />
      <span className="font-body font-800 text-xs tracking-[0.2em]">
        CPH NOW: {wx.temp}° · {label}
      </span>
      {rain && <ArrowRight size={13} className="text-[#1E3A8A]" />}
    </>
  );

  const cls =
    "inline-flex items-center gap-2 " +
    (rain ? "text-[#1E3A8A] hover:opacity-70 transition-opacity" : "");

  return rain ? (
    <Link to="/rainy-day" data-testid="weather-badge" title="It's raining in Copenhagen — see the rainy day guide" className={cls}>
      {inner}
    </Link>
  ) : (
    <span data-testid="weather-badge" className={cls}>{inner}</span>
  );
};
